import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelectedDate } from '../context/DateContext';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, RefreshCw, Filter, GraduationCap } from 'lucide-react';
import {
  format,
  addDays,
  subDays,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  isSameMonth,
  parseISO,
  isToday,
  isSameDay,
  isTomorrow,
} from 'date-fns';
import { useEventsRange, useFamilyMembers, useDisplaySettings, useFamily } from '../api/hooks';
import { EventPreviewModal } from './EventPreviewModal';
import { useIsPhone, useIsNarrowDesktop } from '../hooks/useMediaQuery';
import { useSchoolDate } from '../hooks/useSchoolDate';
import { useWeather } from '../hooks/useWeather';
import { AgendaMobileView } from '../components/AgendaMobileView';
import { MonthMobileView } from '../components/MonthMobileView';
// BottomSheet removed — school view no longer uses mobile bottom sheet
import { EventCard, MemberDotsStack } from '../components/calendar';
import { analytics, trackEvent } from '../lib/analytics';
import { getNowInTz } from '../lib/timezone';
import { calculateTimedViewDensity } from '../lib/event-density';
import { deduplicateEvents, type DeduplicatedEvent } from '../lib/event-deduplication';
import { assignCrossMemberLanes, assignEventLanes } from '../lib/event-lanes';
import {
  isEventPast,
  isMultiDayEvent,
  getEventDayPortion,
  formatMultiDayRange,
} from '../lib/event-utils';
import type { CalendarEvent, Member } from '../types/calendar';
// SchoolChecklistPanel removed from main layout — retained for settings only (see SchoolChecklistSettings)
import { SchoolChecklistSettings } from '../components/school/SchoolChecklistSettings';
import { SchoolRemindersView } from '../components/school/SchoolRemindersView';
import { useSchoolReminders } from '../hooks/useSchoolReminders';

/**
 * Determine if an event is from a school timetable source.
 * School timetable events should be hidden from week/month/day views but visible in school view.
 */
function isSchoolTimetableEvent(event: CalendarEvent): boolean {
  // Any compass source is a school timetable
  if (event.sourceType === 'compass') return true;
  
  // ICS sources with "timetable" in the name are school timetables
  if (event.sourceType === 'ics' && event.sourceName) {
    const lowerName = event.sourceName.toLowerCase();
    return lowerName.includes('timetable');
  }
  
  return false;
}

/**
 * Generate CSS border style for event left border.
 * Single member: solid color. Multiple members: vertical gradient segments.
 */
function getEventBorderStyle(members: Member[]): React.CSSProperties {
  if (members.length <= 1) {
    return {
      borderLeft: `4px solid ${members[0]?.color || '#ccc'}`,
    };
  }
  // Equal vertical segments for each member color
  const stops = members
    .map((m, i) => {
      const start = (i / members.length) * 100;
      const end = ((i + 1) / members.length) * 100;
      return `${m.color} ${start}% ${end}%`;
    })
    .join(', ');
  return {
    borderLeft: '4px solid transparent',
    borderImage: `linear-gradient(to bottom, ${stops}) 1`,
  };
}

type ViewMode = 'day' | 'week' | 'month' | 'school';

/**
 * Determine the smart default view mode based on time of day.
 * School days (Mon-Fri):
 *   6am–9am  → school view (morning prep)
 *   9am–4pm  → week view (normal day)
 *   4pm–9pm  → school view (evening prep for next day)
 *   9pm–6am  → week view
 * Weekends/holidays → week view
 */
function getSmartDefaultView(): ViewMode {
  const now = new Date();
  const hour = now.getHours();
  const day = now.getDay(); // 0=Sun, 6=Sat
  const isWeekday = day >= 1 && day <= 5; // Mon-Fri
  const tomorrowIsSchoolDay = day >= 0 && day <= 4; // Sun-Thu (next day is Mon-Fri)

  // Morning prep: 6am–9am on school days (Mon-Fri)
  if (isWeekday && hour >= 6 && hour < 9) return 'school';

  // Evening prep: 4pm–9pm when tomorrow is a school day (Sun-Thu)
  if (tomorrowIsSchoolDay && hour >= 16 && hour < 21) return 'school';

  return 'week';
}

const DEFAULT_START_HOUR = 6;
const DEFAULT_END_HOUR = 21;
const DEFAULT_SCHOOL_START_HOUR = 7;
const DEFAULT_SCHOOL_END_HOUR = 19;
const HOUR_HEIGHT = 60;

export function CalendarPage() {
  // Use shared date context so mini calendar in sidebar can navigate
  const { selectedDate: currentDate, setSelectedDate: setCurrentDate } = useSelectedDate();
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>(() => getSmartDefaultView());
  const isPhone = useIsPhone();
  const isNarrowDesktop = useIsNarrowDesktop();
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [showSchoolTimetable, setShowSchoolTimetable] = useState<boolean>(() => {
    // Load from localStorage, default to false (hide school timetable)
    const saved = localStorage.getItem('showSchoolTimetable');
    return saved === 'true';
  });
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showSchoolSettings, setShowSchoolSettings] = useState(false);
  // showMobileChecklist removed — school view no longer uses bottom sheet
  // Track whether school view filter was applied (so we don't re-apply on every render)
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedEventMembers, setSelectedEventMembers] = useState<Member[] | undefined>();
  // Track whether to use rolling week (today + next 6 days) vs calendar week (Mon-Sun)
  const [useRollingWeek, setUseRollingWeek] = useState(true);

  // Persist showSchoolTimetable to localStorage
  useEffect(() => {
    localStorage.setItem('showSchoolTimetable', String(showSchoolTimetable));
  }, [showSchoolTimetable]);

  // Read URL query parameters and update date/view accordingly
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const viewParam = searchParams.get('view') as ViewMode | null;

    if (dateParam) {
      try {
        const parsedDate = parseISO(dateParam);
        if (!isNaN(parsedDate.getTime())) {
          setCurrentDate(parsedDate);
          // When navigating via mini calendar (date param present), use calendar week
          if (viewParam === 'week' || viewMode === 'week') {
            setUseRollingWeek(false);
          }
        }
      } catch (error) {
        console.error('Invalid date parameter:', dateParam);
      }
    }

    if (viewParam && ['day', 'week', 'month', 'school'].includes(viewParam)) {
      setViewMode(viewParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Re-run when URL params change

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const { data: eventsData, isLoading, refetch, isRefetching } = useEventsRange(365, 30);
  const { data: membersData } = useFamilyMembers();
  const { data: displaySettings } = useDisplaySettings();
  const { data: familyData } = useFamily();

  const members = eventsData?.members || membersData || [];
  const eventsByDate = eventsData?.eventsByDate || {};

  // Filter out school timetable events for non-school views (unless toggle is ON)
  const filteredEventsByDate = useMemo(() => {
    // School view OR toggle is ON: show everything (including school timetable)
    if (viewMode === 'school' || showSchoolTimetable) {
      return eventsByDate;
    }
    
    // Week/Month/Day views with toggle OFF: filter out school timetable events
    const filtered: typeof eventsByDate = {};
    
    for (const dateKey in eventsByDate) {
      filtered[dateKey] = {};
      
      for (const memberId in eventsByDate[dateKey]) {
        const memberEvents = eventsByDate[dateKey]![memberId] || [];
        filtered[dateKey]![memberId] = memberEvents.filter(event => !isSchoolTimetableEvent(event));
      }
    }
    
    return filtered;
  }, [eventsByDate, viewMode, showSchoolTimetable]);

  // Get week start preference (0 = Sunday, 1 = Monday)
  const weekStartsOn = displaySettings?.weekStartsOn ?? 0;

  // Helper to format time based on user preference
  const formatTime = (date: Date | string, includeAmPm = true) => {
    const d = typeof date === 'string' ? parseISO(date) : date;
    const timeFormat = displaySettings?.timeFormat || '12h';
    if (timeFormat === '24h') {
      return format(d, 'HH:mm');
    }
    return format(d, includeAmPm ? 'h:mm a' : 'h:mm');
  };

  // --- Smart School Date Logic ---
  const familyTimezone = familyData?.timezone || 'Australia/Melbourne';

  const {
    schoolDate,
    schoolDateLabel,
    schoolDateSubtitle,
    handleSchoolPrev,
    handleSchoolNext,
    handleSchoolReset,
  } = useSchoolDate(familyTimezone, viewMode);

  // Weather data for Melbourne, Australia
  const { data: weather } = useWeather(-37.8136, 144.9631, familyTimezone);

  // The effective date used for the school view's events & checklist
  // School view always uses unfiltered events (to show timetable events)
  const schoolViewDate = viewMode === 'school' ? schoolDate : currentDate;
  const schoolViewDateKey = format(schoolViewDate, 'yyyy-MM-dd');
  const schoolViewEventsByMember = eventsByDate[schoolViewDateKey] || {};

  // School reminders view — derived from checklist + Compass events
  const schoolChildMembers = members.filter((m) => m.type === 'child' || m.type === 'teen');
  const { remindersByMember: schoolRemindersByMember, isLoading: schoolRemindersLoading } = useSchoolReminders(
    schoolViewDate,
    schoolChildMembers.map((m) => m.id),
    schoolViewEventsByMember,
  );

  // Apply default member filters per view mode
  // Track which view we last applied defaults for so we don't fight user selections
  const [lastDefaultedView, setLastDefaultedView] = useState<ViewMode | null>(null);

  useEffect(() => {
    if (members.length === 0 || viewMode === lastDefaultedView) return;

    if (viewMode === 'school') {
      const childIds = members
        .filter((m) => m.type === 'child' || m.type === 'teen')
        .map((m) => m.id);
      setSelectedMemberIds(childIds);
      trackEvent('school_view_activated');
    } else if (viewMode === 'day') {
      // Day view: show all family members
      setSelectedMemberIds([]);
    } else if (viewMode === 'week' || viewMode === 'month') {
      // Week/Month view: default to adults only
      const adultIds = members
        .filter((m) => m.type === 'adult')
        .map((m) => m.id);
      setSelectedMemberIds(adultIds);
    }

    setLastDefaultedView(viewMode);
  }, [viewMode, members, lastDefaultedView]);

  // Filter members based on selection
  // In week/month/day views, always include children if they have non-school events
  // (e.g. doctor appointments, parties from adult calendars re-attributed to kids)
  const filteredMembers = viewMode === 'school'
    ? members.filter((m) => m.type === 'child' || m.type === 'teen')
    : selectedMemberIds.length > 0
      ? members.filter((m) => selectedMemberIds.includes(m.id) || m.type === 'child' || m.type === 'teen')
      : members;

  // Dynamic display hours based on view mode + settings, expanding for out-of-range events
  const { displayStartHour, displayEndHour, hours } = useMemo(() => {
    // Base hours from settings
    const baseStart = viewMode === 'school'
      ? (displaySettings?.schoolStartHour ?? DEFAULT_SCHOOL_START_HOUR)
      : (displaySettings?.startHour ?? DEFAULT_START_HOUR);
    const baseEnd = viewMode === 'school'
      ? (displaySettings?.schoolEndHour ?? DEFAULT_SCHOOL_END_HOUR)
      : (displaySettings?.endHour ?? DEFAULT_END_HOUR);

    // Check if any visible events fall outside the base range
    // Use appropriate data based on viewMode
    const currentDateKey = format(viewMode === 'school' ? schoolViewDate : currentDate, 'yyyy-MM-dd');
    const dayEvents = viewMode === 'school' 
      ? (eventsByDate[currentDateKey] || {})
      : (filteredEventsByDate[currentDateKey] || {});
    let minHour = baseStart;
    let maxHour = baseEnd;

    for (const memberId of filteredMembers.map(m => m.id)) {
      const memberEvents = dayEvents[memberId] || [];
      for (const event of memberEvents) {
        if (event.allDay) continue;
        const startH = new Date(event.startTime).getHours();
        const endH = new Date(event.endTime).getHours() + (new Date(event.endTime).getMinutes() > 0 ? 1 : 0);
        if (startH < minHour) minHour = startH;
        if (endH > maxHour) maxHour = Math.min(endH, 23);
      }
    }

    const hoursArray = Array.from(
      { length: maxHour - minHour + 1 },
      (_, i) => i + minHour
    );

    return { displayStartHour: minHour, displayEndHour: maxHour, hours: hoursArray };
  }, [viewMode, displaySettings, filteredMembers, eventsByDate, filteredEventsByDate, currentDate, schoolViewDate]);

  const handlePrev = () => {
    if (viewMode === 'school') return handleSchoolPrev();
    if (viewMode === 'day') setCurrentDate(subDays(currentDate, 1));
    else if (viewMode === 'week') {
      // In week view, always navigate by 7 days (whether rolling or calendar week)
      const newDate = subDays(currentDate, 7);
      setCurrentDate(newDate);
      // Check if new date contains today - if so, switch back to rolling week
      const now = new Date();
      const todayStart = startOfDay(now);
      const weekStart = useRollingWeek && isSameDay(newDate, todayStart) 
        ? todayStart 
        : startOfWeek(newDate, { weekStartsOn });
      const weekEnd = addDays(weekStart, 6);
      if (todayStart >= weekStart && todayStart <= weekEnd) {
        setUseRollingWeek(true);
      }
    }
    else setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNext = () => {
    if (viewMode === 'school') return handleSchoolNext();
    if (viewMode === 'day') setCurrentDate(addDays(currentDate, 1));
    else if (viewMode === 'week') {
      // In week view, always navigate by 7 days (whether rolling or calendar week)
      const newDate = addDays(currentDate, 7);
      setCurrentDate(newDate);
      // Check if new date contains today - if so, switch back to rolling week
      const now = new Date();
      const todayStart = startOfDay(now);
      const weekStart = useRollingWeek && isSameDay(newDate, todayStart) 
        ? todayStart 
        : startOfWeek(newDate, { weekStartsOn });
      const weekEnd = addDays(weekStart, 6);
      if (todayStart >= weekStart && todayStart <= weekEnd) {
        setUseRollingWeek(true);
      }
    }
    else setCurrentDate(addMonths(currentDate, 1));
  };

  const handleToday = () => {
    if (viewMode === 'school') return handleSchoolReset();
    setCurrentDate(new Date());
    // When clicking "Today", switch to rolling week mode
    if (viewMode === 'week') {
      setUseRollingWeek(true);
    }
  };

  const toggleMemberFilter = (memberId: string) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Calculate total filter count — only count active deviations from default
  // Default = all members shown + school timetable hidden, so only count:
  // - member filters when some members are deselected
  // - school timetable only when it's toggled ON (non-default)
  const filterCount = useMemo(() => {
    let count = selectedMemberIds.length;
    if (viewMode !== 'school' && showSchoolTimetable) {
      count++;
    }
    return count;
  }, [selectedMemberIds.length, viewMode, showSchoolTimetable]);

  const openEventPreview = (event: CalendarEvent, members?: Member[]) => {
    setSelectedEvent(event);
    setSelectedEventMembers(members);
    analytics.calendarEventViewed(event.id, viewMode);
  };

  const closeEventPreview = () => {
    setSelectedEvent(null);
    setSelectedEventMembers(undefined);
  };

  const getEventStyle = (event: CalendarEvent, currentDate: Date) => {
    const portion = getEventDayPortion(event, currentDate);
    if (!portion) return { top: 0, height: 24 }; // Shouldn't happen after filtering

    const start = parseISO(event.startTime);
    const end = parseISO(event.endTime);

    // Determine actual display times for this day
    let displayStart: Date;
    let displayEnd: Date;

    if (portion.isStartDay && portion.isEndDay) {
      // Single day event or same-day portion
      displayStart = start;
      displayEnd = end;
    } else if (portion.isStartDay) {
      // First day of multi-day: show from start time to end of day
      displayStart = start;
      displayEnd = endOfDay(currentDate);
    } else if (portion.isEndDay) {
      // Last day of multi-day: show from start of day to end time
      displayStart = startOfDay(currentDate);
      displayEnd = end;
    } else {
      // Middle day: treat as all-day (will be filtered out in timed events)
      return null as unknown as { top: number; height: number }; // Will be shown in all-day section instead
    }

    const startHour = displayStart.getHours() + displayStart.getMinutes() / 60;
    const endHour = displayEnd.getHours() + displayEnd.getMinutes() / 60;
    const top = Math.max(0, (startHour - displayStartHour) * HOUR_HEIGHT);
    const height = Math.max(24, (endHour - startHour) * HOUR_HEIGHT);
    return { top, height };
  };

  const currentTimeTop = useMemo(() => {
    const hour = currentTime.getHours() + currentTime.getMinutes() / 60;
    if (hour < displayStartHour || hour > displayEndHour) return null;
    return (hour - displayStartHour) * HOUR_HEIGHT;
  }, [currentTime, displayStartHour, displayEndHour]);

  // Week days for week view
  // Use rolling week (today + next 6 days) when useRollingWeek is true and viewing today's week
  // Otherwise use standard calendar week (Mon-Sun or Sun-Sat based on weekStartsOn)
  const weekDays = useMemo(() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    
    if (useRollingWeek && isSameDay(currentDate, todayStart)) {
      // Rolling week: show today + next 6 days
      return eachDayOfInterval({ 
        start: todayStart, 
        end: addDays(todayStart, 6) 
      });
    }
    
    // Standard calendar week
    const start = startOfWeek(currentDate, { weekStartsOn });
    return eachDayOfInterval({ start, end: endOfWeek(start, { weekStartsOn }) });
  }, [currentDate, weekStartsOn, useRollingWeek]);

  // Month days for month view
  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate, weekStartsOn]);

  // Extract duplicate "Today" button logic for main button and shortcut
  const todayButtonState = useMemo(() => {
    const nowTz = getNowInTz(familyTimezone);
    const todayStart = startOfDay(nowTz);
    let isViewingToday: boolean;
    let buttonLabel: string;

    if (viewMode === 'day') {
      isViewingToday = isSameDay(currentDate, todayStart);
      if (isViewingToday) {
        buttonLabel = 'Today';
      } else if (isTomorrow(currentDate)) {
        buttonLabel = 'Tomorrow';
      } else {
        buttonLabel = format(currentDate, 'EEE d MMM');
      }
    } else if (viewMode === 'week') {
      // Determine the actual week start based on rolling vs calendar week
      const wkStart = (useRollingWeek && isSameDay(currentDate, todayStart))
        ? todayStart
        : startOfWeek(currentDate, { weekStartsOn });
      const wkEnd = addDays(wkStart, 6);
      
      isViewingToday = todayStart >= wkStart && todayStart <= wkEnd;
      if (isViewingToday) {
        buttonLabel = 'This Week';
      } else {
        buttonLabel = format(wkStart, 'd MMM');
      }
    } else {
      // month
      isViewingToday = currentDate.getMonth() === todayStart.getMonth() && currentDate.getFullYear() === todayStart.getFullYear();
      if (isViewingToday) {
        buttonLabel = 'This Month';
      } else {
        buttonLabel = format(currentDate, 'MMM yyyy');
      }
    }

    return { isViewingToday, buttonLabel };
  }, [currentDate, viewMode, familyTimezone, weekStartsOn, useRollingWeek]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="relative">
        <div className={`bg-bg-surface border-b border-border-default ${isPhone ? 'px-3 py-2' : 'px-6 py-5'}`}>
        {isPhone ? (
          <>
            {/* Mobile: Row 1 - Navigation */}
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-1">
                <button onClick={handlePrev} className="p-2.5 rounded-xl hover:bg-bg-secondary">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                {/* Date label / Today button */}
                {viewMode === 'school' ? (
                  <button
                    onClick={handleSchoolReset}
                    className={`btn-today flex flex-col items-center leading-tight ${
                      schoolDateLabel !== 'Today'
                        ? 'bg-[#f59e0b] text-white border-[#f59e0b]'
                        : ''
                    }`}
                    title="Reset to smart default"
                  >
                    <span className="text-sm font-semibold">{schoolDateLabel}</span>
                    <span className={`text-xs ${
                      schoolDateLabel !== 'Today' ? 'text-white/80' : 'text-text-secondary'
                    }`}>{schoolDateSubtitle}</span>
                  </button>
                ) : (
                  <button
                    onClick={handleToday}
                    className={`btn-today ${!todayButtonState.isViewingToday ? 'bg-[#f59e0b] text-white border-[#f59e0b]' : ''}`}
                    title={todayButtonState.isViewingToday ? undefined : 'Go to today'}
                  >
                    {todayButtonState.buttonLabel}
                  </button>
                )}
                
                <button onClick={handleNext} className="p-2.5 rounded-xl hover:bg-bg-secondary">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex items-center gap-1.5">
                {/* ↩ Today shortcut when not viewing today */}
                {viewMode === 'school' && schoolDateLabel !== 'Today' && (
                  <button onClick={handleSchoolReset} className="px-2 py-1 text-xs text-teal font-semibold rounded-lg border border-teal/20 bg-teal/5">
                    ↩ Today
                  </button>
                )}
                {viewMode !== 'school' && !todayButtonState.isViewingToday && (
                  <button onClick={handleToday} className="px-2 py-1 text-xs text-teal font-semibold rounded-lg border border-teal/20 bg-teal/5">
                    ↩ Today
                  </button>
                )}
                
                {/* Compact weather */}
                {weather && (
                  <a
                    href="https://open-meteo.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
                    title="Weather by Open-Meteo.com"
                  >
                    <span>{weather.icon}</span>
                    <span>{weather.tempMin}°/{weather.tempMax}°</span>
                  </a>
                )}
              </div>
            </div>
            
            {/* Mobile: Row 2 - View mode + controls */}
            <div className="flex items-center justify-between">
              {/* View mode pills */}
              <div className="flex bg-bg-primary rounded-xl p-0.5">
                {(['day', 'week', 'month'] as ViewMode[]).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      viewMode === mode ? 'bg-teal text-white shadow-sm' : 'text-text-secondary'
                    }`}
                  >
                    {mode.charAt(0).toUpperCase()}
                  </button>
                ))}
                <button
                  onClick={() => setViewMode('school')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 ${
                    viewMode === 'school' ? 'bg-teal text-white shadow-sm' : 'text-text-secondary'
                  }`}
                >
                  <GraduationCap className="w-3.5 h-3.5" />
                </button>
              </div>
              
              {/* Filter + Refresh */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                  className="p-2 rounded-xl hover:bg-bg-secondary relative"
                  title="Filter by member"
                >
                  <Filter className="w-4 h-4" />
                  {filterCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-teal text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {filterCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => refetch()}
                  className="p-2 rounded-xl hover:bg-bg-secondary"
                  disabled={isRefetching}
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Desktop: Keep existing layout — compact on narrow desktop (iPad mini) */
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <h1 className="font-display text-display-sm lg:text-display-md font-semibold text-text-primary whitespace-nowrap">Calendar</h1>
              {weather && (
                <div
                  className="weather-card flex items-center gap-2 px-3 py-2 md:gap-3 md:px-4 md:py-2.5 lg:px-6 lg:py-4 rounded-xl border border-sky-200"
                  style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' }}
                  title={`${weather.description} - Weather by Open-Meteo.com`}
                >
                  <span className="text-2xl md:text-3xl lg:text-4xl leading-none">{weather.icon}</span>
                  <div>
                    <div className="text-base md:text-lg lg:text-2xl font-bold text-sky-900">
                      {weather.tempMin}° / {weather.tempMax}°
                    </div>
                    {/* Hide description text on narrow desktop to save header space */}
                    <div className={`text-xs md:text-sm lg:text-base font-medium text-sky-700 ${isNarrowDesktop ? 'hidden' : ''}`}>
                      {weather.description}
                    </div>
                    <a
                      href="https://open-meteo.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-[10px] text-sky-500 hover:text-sky-600 hover:underline ${isNarrowDesktop ? 'hidden' : ''}`}
                    >
                      Weather by Open-Meteo.com
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5 lg:gap-3 shrink-0">
              {/* View Mode Toggle — abbreviated labels on narrow desktop to save space */}
              <div className="flex bg-bg-primary rounded-xl p-1 shadow-sm">
                {(['day', 'week', 'month'] as ViewMode[]).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    title={mode.charAt(0).toUpperCase() + mode.slice(1)}
                    className={`px-2 py-2 lg:px-4 text-sm rounded-lg font-semibold transition-all duration-200 ${
                      viewMode === mode
                        ? 'bg-teal text-white shadow-sm'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                    }`}
                  >
                    {isNarrowDesktop ? mode.charAt(0).toUpperCase() : mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
                <button
                  onClick={() => setViewMode('school')}
                  aria-label="School view"
                  title="School"
                  className={`px-2 py-2 lg:px-4 text-sm rounded-lg font-semibold transition-all duration-200 flex items-center gap-1 lg:gap-1.5 ${
                    viewMode === 'school'
                      ? 'bg-teal text-white shadow-sm'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                  }`}
                >
                  <GraduationCap className="w-4 h-4" />
                  {!isNarrowDesktop && 'School'}
                </button>
              </div>

              {/* Filter */}
              <div className="relative">
                <button
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                  className={`btn-icon ${filterCount > 0 ? 'bg-teal/10 text-teal' : ''}`}
                  title="Filter by member"
                >
                  <Filter className="w-5 h-5" />
                  {filterCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal text-white text-xs font-semibold rounded-full flex items-center justify-center shadow-sm">
                      {filterCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Refresh */}
              <button
                onClick={() => refetch()}
                className="btn-icon md:p-3"
                disabled={isRefetching}
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 md:w-6 md:h-6 ${isRefetching ? 'animate-spin' : ''}`} />
              </button>

              {/* Navigation */}
              <div className="flex items-center gap-2">
                <button onClick={handlePrev} className="btn-icon hover:bg-bg-secondary md:p-3">
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                </button>
                {viewMode === 'school' ? (
                  <button
                    onClick={handleSchoolReset}
                    className={`btn-today flex flex-col items-center leading-tight ${
                      schoolDateLabel !== 'Today'
                        ? 'bg-[#f59e0b] text-white border-[#f59e0b]'
                        : ''
                    }`}
                    title="Reset to smart default"
                  >
                    <span className="text-base font-semibold">{schoolDateLabel}</span>
                    <span className={`text-sm font-normal ${
                      schoolDateLabel !== 'Today' ? 'text-white/80' : 'text-text-secondary'
                    }`}>{schoolDateSubtitle}</span>
                  </button>
                ) : (
                  <button
                    onClick={handleToday}
                    className={`btn-today ${!todayButtonState.isViewingToday ? 'bg-[#f59e0b] text-white border-[#f59e0b]' : ''}`}
                    title={todayButtonState.isViewingToday ? undefined : 'Go to today'}
                  >
                    {todayButtonState.buttonLabel}
                  </button>
                )}
                <button onClick={handleNext} className="btn-icon hover:bg-bg-secondary md:p-3">
                  <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                </button>
                {viewMode === 'school' && schoolDateLabel !== 'Today' && (
                  <button onClick={handleSchoolReset} className="px-3 py-1.5 text-sm text-teal font-semibold rounded-lg border border-teal/20 bg-teal/5 hover:bg-teal/10 transition-colors">
                    ↩ Today
                  </button>
                )}
                {viewMode !== 'school' && !todayButtonState.isViewingToday && (
                  <button onClick={handleToday} className="px-3 py-1.5 text-sm text-teal font-semibold rounded-lg border border-teal/20 bg-teal/5 hover:bg-teal/10 transition-colors">
                    ↩ Today
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
        
        {/* Filter menu dropdown (renders below header for both mobile and desktop) */}
        {showFilterMenu && (
          <div className="absolute right-3 top-full mt-2 bg-white rounded-2xl shadow-elevated border border-border-default p-3 z-50 min-w-[220px]">
            <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide px-2 py-1 mb-2">
              Filter by member
            </div>
            {members.map((member) => (
              <button
                key={member.id}
                onClick={() => toggleMemberFilter(member.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                  selectedMemberIds.includes(member.id) ? 'bg-teal/10' : 'hover:bg-bg-secondary'
                }`}
              >
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-semibold text-white shadow-sm"
                  style={{ backgroundColor: member.color }}
                >
                  {member.name.substring(0, 2)}
                </div>
                <span className="text-sm font-medium text-text-primary">{member.name}</span>
                {selectedMemberIds.includes(member.id) && (
                  <span className="ml-auto text-teal font-bold">✓</span>
                )}
              </button>
            ))}
            
            {/* School timetable toggle (only show in non-school views) */}
            {viewMode !== 'school' && (
              <div className="mt-3 pt-3 border-t border-border-light">
                <button
                  onClick={() => setShowSchoolTimetable(!showSchoolTimetable)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-bg-secondary transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <GraduationCap className="w-5 h-5 text-text-secondary" />
                    <span className="text-sm font-medium text-text-primary">School timetable</span>
                  </div>
                  <div
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showSchoolTimetable ? 'bg-teal' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                        showSchoolTimetable ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </div>
                </button>
              </div>
            )}
            
            {(selectedMemberIds.length > 0 || (viewMode !== 'school' && showSchoolTimetable)) && (
              <button
                onClick={() => {
                  setSelectedMemberIds([]);
                  setShowSchoolTimetable(false);
                }}
                className="w-full text-xs font-medium text-coral hover:text-coral-dark mt-3 pt-3 border-t border-border-light transition-colors"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Calendar Content */}
      <div className={`flex-1 overflow-hidden ${viewMode === 'school' ? '' : 'flex'}`}>
        {/* School Reminders View — full-width, replaces timetable + checklist sidebar */}
        {viewMode === 'school' && (
          <div className="h-full overflow-auto">
            {schoolRemindersLoading ? (
              <div className="flex items-center justify-center h-full">
                <RefreshCw className="w-8 h-8 animate-spin text-text-muted" />
              </div>
            ) : (
              <SchoolRemindersView
                date={schoolViewDate}
                members={schoolChildMembers}
                remindersByMember={schoolRemindersByMember}
                // TODO: wire subjectsByMember and flaggedSubjectsByMember once a
                // Compass subjects-per-day endpoint exists. The subjects strip in
                // each kid card will remain empty until that data is available.
                // See: types/schoolReminders.ts SubjectsByDay / FlaggedSubjects
              />
            )}
          </div>
        )}
        <div className={`${viewMode !== 'school' ? 'h-full overflow-auto flex-1' : 'hidden'}`}>
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-8 h-8 animate-spin text-text-muted" />
            </div>
          ) : isPhone && viewMode === 'month' ? (
            /* Mobile Month View — compact dot grid */
            <MonthMobileView
              currentDate={currentDate}
              members={filteredMembers}
              eventsByDate={filteredEventsByDate}
              onDayClick={(date) => {
                setCurrentDate(date);
                setViewMode('day');
              }}
              onPrevMonth={() => setCurrentDate(subMonths(currentDate, 1))}
              onNextMonth={() => setCurrentDate(addMonths(currentDate, 1))}
              weekStartsOn={weekStartsOn}
            />
          ) : isPhone ? (
            /* Mobile Agenda View for phones (<480px) — day, week, school */
            <AgendaMobileView
              date={viewMode === 'school' ? schoolViewDate : currentDate}
              events={(() => {
                if (viewMode === 'school') {
                  // School view: use unfiltered events
                  return eventsByDate[schoolViewDateKey] || {};
                } else if (viewMode === 'week') {
                  // Week mode: use filtered events for the entire week range
                  return filteredEventsByDate;
                } else {
                  // Day mode: use filtered events for single day
                  return filteredEventsByDate[format(currentDate, 'yyyy-MM-dd')] || {};
                }
              })()}
              dateRange={viewMode === 'week' ? { 
                start: startOfWeek(currentDate, { weekStartsOn }), 
                end: endOfWeek(currentDate, { weekStartsOn })
              } : undefined}
              members={filteredMembers}
              formatTime={formatTime}
              onEventClick={(event, member) =>
                openEventPreview(member ? { ...event, member } : event)
              }
              onSwipeLeft={handleNext}
              onSwipeRight={handlePrev}
            />
          ) : viewMode === 'day' ? (
            <DayView
              date={currentDate}
              members={filteredMembers}
              eventsByDate={filteredEventsByDate}
              getEventStyle={getEventStyle}
              currentTimeTop={currentTimeTop}
              formatTime={formatTime}
              onEventClick={openEventPreview}
              hours={hours}
            />
          ) : viewMode === 'week' ? (
            <WeekView
              days={weekDays}
              members={filteredMembers}
              eventsByDate={filteredEventsByDate}
              onDayClick={(date) => {
                setCurrentDate(date);
                setViewMode('day');
              }}
              formatTime={formatTime}
              onEventClick={openEventPreview}
              displayStartHour={displayStartHour}
              displayEndHour={displayEndHour}
              hours={hours}
            />
          ) : (
            <MonthView
              days={monthDays}
              currentDate={currentDate}
              members={filteredMembers}
              eventsByDate={filteredEventsByDate}
              onDayClick={(date) => {
                setCurrentDate(date);
                setViewMode('day');
              }}
              onEventClick={openEventPreview}
            />
          )}
        </div>

        {/* School Checklist Sidebar removed — replaced by SchoolRemindersView above */}
      </div>

      {/* Mobile School View: FAB removed — SchoolRemindersView is inline in the main content area */}

      {/* Event Preview Modal */}
      {selectedEvent && (
        <EventPreviewModal
          event={selectedEvent}
          onClose={closeEventPreview}
          formatTime={formatTime}
          members={selectedEventMembers}
        />
      )}

      {/* School Checklist Settings Modal */}
      {showSchoolSettings && (
        <SchoolChecklistSettings onClose={() => setShowSchoolSettings(false)} />
      )}
    </div>
  );
}

// Day View Component
function DayView({
  date,
  members,
  eventsByDate,
  getEventStyle,
  currentTimeTop,
  formatTime,
  onEventClick,
  hours,
  useMemberColumns = false,
}: {
  date: Date;
  members: Member[];
  eventsByDate: Record<string, Record<string, CalendarEvent[]>>;
  getEventStyle: (event: CalendarEvent, currentDate: Date) => { top: number; height: number } | null;
  currentTimeTop: number | null;
  formatTime: (date: Date | string, includeAmPm?: boolean) => string;
  onEventClick: (event: CalendarEvent) => void;
  hours: number[];
  useMemberColumns?: boolean;
}) {
  const dateKey = format(date, 'yyyy-MM-dd');
  const dayEvents = eventsByDate[dateKey] || {};

  // Assign events to lanes based on actual time overlap (cross-member)
  const { events: timedEventsWithLanes, overflowCount } = useMemo(
    () => assignCrossMemberLanes(members, dayEvents, date, getEventStyle),
    [members, dayEvents, date, getEventStyle]
  );

  // Deduplicate events within each member's list (e.g. same meeting from multiple calendar sources
  // that all get re-attributed to the same child)
  const dedupedDayEvents = useMemo(() => {
    if (!useMemberColumns) return dayEvents;
    const deduped: typeof dayEvents = {};
    for (const memberId in dayEvents) {
      const events = dayEvents[memberId] || [];
      const seen = new Map<string, CalendarEvent>();
      for (const event of events) {
        // Key by title + start time to catch same event from different sources
        const key = `${event.title}|${event.startTime}`;
        if (!seen.has(key)) {
          seen.set(key, event);
        }
      }
      deduped[memberId] = Array.from(seen.values());
    }
    return deduped;
  }, [useMemberColumns, dayEvents]);

  // Per-member lane assignment for school/member-column mode
  const memberLaneData = useMemo(() => {
    if (!useMemberColumns) return null;
    return assignEventLanes(members, dedupedDayEvents, date, getEventStyle);
  }, [useMemberColumns, members, dedupedDayEvents, date, getEventStyle]);

  // Calculate overall density for the day
  const dayDensity = useMemo(() => {
    const allDayEvents = Object.values(dayEvents).flat();
    return calculateTimedViewDensity(allDayEvents, HOUR_HEIGHT);
  }, [dayEvents]);

  // Deduplicate all-day events
  const allDayEvents = useMemo(() => {
    return deduplicateEvents(dayEvents, members).filter((e) => {
      if (e.allDay) return true;
      const portion = getEventDayPortion(e, date);
      return portion?.isMiddleDay;
    });
  }, [dayEvents, members, date]);

  if (members.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-text-secondary gap-4">
        <div className="w-16 h-16 rounded-2xl bg-teal/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <p className="font-medium">Add family members in Settings to see calendars</p>
      </div>
    );
  }

  return (
    <div>
      {/* All-day events section - above time grid */}
      {allDayEvents.length > 0 && (
        <div className="border-b border-border-default bg-bg-surface">
          <div className="flex items-start">
            <div className="w-16 shrink-0 p-2 text-right text-xs font-semibold text-text-secondary uppercase tracking-wide">
              All day
            </div>
            <div className="flex-1 p-2 space-y-1">
              {allDayEvents.slice(0, 3).map((event) => {
                const isMultiDay = isMultiDayEvent(event);
                return (
                  <div
                    key={event.id}
                    onClick={() => onEventClick(event)}
                    className={`text-sm p-2 rounded-lg cursor-pointer hover:shadow-md bg-white shadow-sm border border-border-light transition-shadow ${isEventPast(event) ? 'event-card--past' : ''}`}
                    style={getEventBorderStyle(event.members)}
                  >
                    <div className="flex items-center gap-2">
                      {event.isShared && <MemberDotsStack members={event.members} maxVisible={3} size="md" />}
                      <span className="font-medium line-clamp-2 break-words">{event.title}</span>
                    </div>
                    {isMultiDay && (
                      <span className="text-text-muted text-xs mt-1 block">
                        {formatMultiDayRange(event)}
                      </span>
                    )}
                  </div>
                );
              })}
              {allDayEvents.length > 3 && (
                <div className="text-xs text-text-muted px-2 font-medium">
                  +{allDayEvents.length - 3} more
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Member Column Headers (school/member-column mode) */}
      {useMemberColumns && members.length > 0 && (
        <div className="flex gap-1 sticky top-0 z-10 bg-bg-surface border-b border-border-default">
          <div className="w-16 shrink-0" />
          {members.map((member) => (
            <div
              key={member.id}
              data-testid={`member-column-header-${member.id}`}
              className="flex-1 p-4 flex items-center gap-3 rounded-t-xl"
              style={{ backgroundColor: `${member.color}15` }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-semibold text-white shadow-sm"
                style={{ backgroundColor: member.color }}
              >
                {member.name.substring(0, 2)}
              </div>
              <span className="font-semibold text-text-primary">{member.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Time Grid */}
      <div className="relative">
        {hours.map((hour) => (
          <div key={hour} className="flex border-b border-border-light">
            <div className="w-16 shrink-0 p-2 text-right text-sm text-text-secondary">
              {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
            </div>
            {useMemberColumns ? (
              members.map((member) => (
                <div
                  key={member.id}
                  className="flex-1 border-l border-border-light"
                  style={{ height: HOUR_HEIGHT }}
                />
              ))
            ) : (
              <div
                className="flex-1 border-l border-border-light"
                style={{ height: HOUR_HEIGHT }}
              />
            )}
          </div>
        ))}

        {/* Events Overlay */}
        {useMemberColumns && memberLaneData ? (
          /* Per-member columns for school view */
          <div className="absolute inset-0 left-16 pointer-events-none">
            <div className="relative h-full flex">
              {members.map((member, memberIdx) => {
                const memberEvents = memberLaneData.eventsWithLanes[member.id] || [];
                const memberOverflow = memberLaneData.overflowCounts[member.id] || 0;
                return (
                  <div key={member.id} className="flex-1 relative" style={{ borderLeft: memberIdx > 0 ? '1px solid var(--border-light)' : undefined }}>
                    {memberEvents.map((event) => {
                      const style = getEventStyle(event, date);
                      if (!style) return null;
                      const { laneIndex, totalLanes } = event;
                      const columnWidth = 100 / totalLanes;
                      const leftPercent = laneIndex * columnWidth;
                      const widthPercent = columnWidth;
                      const gap = totalLanes > 1 ? 2 : 0;
                      return (
                        <EventCard
                          key={event.id}
                          event={event}
                          member={member}
                          density={dayDensity.density}
                          formatTime={formatTime}
                          onClick={() => onEventClick(event)}
                          style={{
                            top: style.top,
                            height: style.height,
                            left: `calc(${leftPercent}% + ${gap}px)`,
                            width: `calc(${widthPercent}% - ${gap * 2}px)`,
                            borderLeft: `3px solid ${member.color}`,
                          }}
                          className="absolute pointer-events-auto"
                          isPast={isEventPast(event)}
                        />
                      );
                    })}
                    {memberOverflow > 0 && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-center text-xs text-text-muted font-medium bg-bg-surface/90 rounded px-3 py-1 pointer-events-auto shadow-sm">
                        +{memberOverflow} more
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Cross-member overlap-based columns for day view */
          <div className="absolute inset-0 left-16 pointer-events-none">
            <div className="relative h-full">
              {timedEventsWithLanes.map((event) => {
                const style = getEventStyle(event, date);
                if (!style) return null;

                const { laneIndex, totalLanes } = event;
                const columnWidth = 100 / totalLanes;
                const leftPercent = laneIndex * columnWidth;
                const widthPercent = columnWidth;
                const gap = totalLanes > 1 ? 2 : 0;

                return (
                  <EventCard
                    key={event.id}
                    event={event}
                    member={event.members[0]}
                    density={dayDensity.density}
                    formatTime={formatTime}
                    onClick={() => onEventClick(event)}
                    style={{
                      top: style.top,
                      height: style.height,
                      left: `calc(${leftPercent}% + ${gap}px)`,
                      width: `calc(${widthPercent}% - ${gap * 2}px)`,
                      ...getEventBorderStyle(event.members),
                    }}
                    className="absolute pointer-events-auto"
                    isPast={isEventPast(event)}
                  />
                );
              })}
              {overflowCount > 0 && (
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-center text-xs text-text-muted font-medium bg-bg-surface/90 rounded px-3 py-1 pointer-events-auto shadow-sm">
                  +{overflowCount} more
                </div>
              )}
            </div>
          </div>
        )}

        {/* Current Time Indicator */}
        {currentTimeTop !== null && isToday(date) && (
          <div
            className="absolute left-16 right-0 h-0.5 bg-coral z-20 shadow-coral"
            style={{ top: currentTimeTop }}
          >
            <div className="absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full bg-coral shadow-coral" />
          </div>
        )}
      </div>
    </div>
  );
}

// Week View Component with Timeslots
function WeekView({
  days,
  members,
  eventsByDate,
  onDayClick,
  formatTime,
  onEventClick,
  displayStartHour,
  displayEndHour,
  hours,
}: {
  days: Date[];
  members: Member[];
  eventsByDate: Record<string, Record<string, CalendarEvent[]>>;
  onDayClick: (date: Date) => void;
  formatTime: (date: Date | string, includeAmPm?: boolean) => string;
  onEventClick: (event: CalendarEvent, members?: Member[]) => void;
  displayStartHour: number;
  displayEndHour: number;
  hours: number[];
}) {
  // Helper to determine if a day is a weekend (Saturday or Sunday)
  const isWeekend = (day: Date) => {
    const dayOfWeek = day.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
  };
  const [currentTime, setCurrentTime] = useState(new Date());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolled = useRef(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Mark as mounted after first render so hiddenEventCounts can access the ref
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const currentTimeTop = useMemo(() => {
    const hour = currentTime.getHours() + currentTime.getMinutes() / 60;
    if (hour < displayStartHour || hour > displayEndHour) return null;
    return (hour - displayStartHour) * HOUR_HEIGHT;
  }, [currentTime, displayStartHour, displayEndHour]);

  // Auto-scroll to current time on mount (only if today is visible in the week)
  const todayIsVisible = days.some((day) => isToday(day));
  useEffect(() => {
    if (!hasAutoScrolled.current && scrollContainerRef.current && currentTimeTop !== null && todayIsVisible) {
      const scrollPosition = Math.max(0, currentTimeTop - 100);
      scrollContainerRef.current.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      hasAutoScrolled.current = true;
    }
  }, [currentTimeTop, todayIsVisible]);

  // Reset auto-scroll flag when days change (navigating to different week)
  useEffect(() => {
    hasAutoScrolled.current = false;
  }, [days]);

  // Track scroll position to show/hide scroll indicators
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setCanScrollUp(scrollTop > 10);
      setCanScrollDown(scrollTop + clientHeight < scrollHeight - 10);
    };

    updateScrollState();
    container.addEventListener('scroll', updateScrollState, { passive: true });
    return () => container.removeEventListener('scroll', updateScrollState);
  }, []);

  const getEventStyle = (event: CalendarEvent, currentDate: Date) => {
    const portion = getEventDayPortion(event, currentDate);
    if (!portion) return { top: 0, height: 20 }; // Shouldn't happen after filtering

    const start = parseISO(event.startTime);
    const end = parseISO(event.endTime);

    // Determine actual display times for this day
    let displayStart: Date;
    let displayEnd: Date;

    if (portion.isStartDay && portion.isEndDay) {
      // Single day event or same-day portion
      displayStart = start;
      displayEnd = end;
    } else if (portion.isStartDay) {
      // First day of multi-day: show from start time to end of day
      displayStart = start;
      displayEnd = endOfDay(currentDate);
    } else if (portion.isEndDay) {
      // Last day of multi-day: show from start of day to end time
      displayStart = startOfDay(currentDate);
      displayEnd = end;
    } else {
      // Middle day: treat as all-day (will be filtered out in timed events)
      return null as unknown as { top: number; height: number };
    }

    const startHour = displayStart.getHours() + displayStart.getMinutes() / 60;
    const endHour = displayEnd.getHours() + displayEnd.getMinutes() / 60;
    const top = Math.max(0, (startHour - displayStartHour) * HOUR_HEIGHT);
    const height = Math.max(20, (endHour - startHour) * HOUR_HEIGHT);
    return { top, height };
  };

  // Assign events to horizontal lanes (columns) for side-by-side display
  // Uses greedy algorithm similar to timeline view but applied per day
  // MAX 3 lanes for UX - overflow events accessible via Day view
  const timedEventsWithLanes = useMemo(() => {
    const result: Record<string, Array<DeduplicatedEvent & { laneIndex: number; totalLanes: number; isOverflow: boolean }>> = {};
    const overflowCounts: Record<string, number> = {};

    for (const day of days) {
      const dateKey = format(day, 'yyyy-MM-dd');
      const dayEvents = eventsByDate[dateKey] || {};

      const { events: laneEvents, overflowCount } = assignCrossMemberLanes(
        members,
        dayEvents,
        day,
        getEventStyle
      );

      result[dateKey] = laneEvents.map((e) => ({ ...e, isOverflow: false }));
      overflowCounts[dateKey] = overflowCount;
    }

    return { eventsWithLanes: result, overflowCounts };
  }, [days, eventsByDate, members]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect and deduplicate all-day events for all days
  // Include middle days of multi-day events
  const allDayEventsByDay = useMemo(() => {
    const result: Record<string, DeduplicatedEvent[]> = {};
    for (const day of days) {
      const dateKey = format(day, 'yyyy-MM-dd');
      const dayEvents = eventsByDate[dateKey] || {};
      // Deduplicate events across members
      const deduped = deduplicateEvents(dayEvents, members);
      result[dateKey] = deduped.filter((e) => {
        if (e.allDay) return true;
        // Include middle days of multi-day events
        const portion = getEventDayPortion(e, day);
        return portion?.isMiddleDay;
      });
    }
    return result;
  }, [days, members, eventsByDate]);

  const hasAllDayEvents = Object.values(allDayEventsByDay).some((events) => events.length > 0);

  // Count timed events hidden above/below viewport for each day
  const hiddenEventCounts = useMemo(() => {
    let above = 0;
    let below = 0;
    const container = scrollContainerRef.current;
    if (!container || !isMounted) return { above: 0, below: 0 };

    const viewportTop = container.scrollTop;
    const viewportBottom = viewportTop + container.clientHeight;

    for (const day of days) {
      const dateKey = format(day, 'yyyy-MM-dd');
      const dayEvents = eventsByDate[dateKey] || {};
      const timedEvents = deduplicateEvents(dayEvents, members).filter((e) => {
        if (e.allDay) return false;
        // Filter out middle days
        const portion = getEventDayPortion(e, day);
        return !portion?.isMiddleDay;
      });

      for (const event of timedEvents) {
        const style = getEventStyle(event, day);
        if (!style) continue; // Skip if null
        if (style.top + style.height < viewportTop) above++;
        else if (style.top > viewportBottom) below++;
      }
    }
    return { above, below };
  }, [days, eventsByDate, members, canScrollUp, canScrollDown, isMounted]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollUp = () => {
    scrollContainerRef.current?.scrollBy({ top: -HOUR_HEIGHT * 2, behavior: 'smooth' });
  };

  const scrollDown = () => {
    scrollContainerRef.current?.scrollBy({ top: HOUR_HEIGHT * 2, behavior: 'smooth' });
  };

  return (
    <div className="h-full flex flex-col relative">
      {/* Scroll indicators */}
      {canScrollUp && hiddenEventCounts.above > 0 && (
        <button
          onClick={scrollUp}
          className="absolute top-0 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-b-xl shadow-lg border border-t-0 border-border-default text-sm font-medium text-teal hover:bg-teal/5 transition-colors"
        >
          <ChevronUp className="w-4 h-4" />
          <span>{hiddenEventCounts.above} earlier</span>
        </button>
      )}
      {canScrollDown && hiddenEventCounts.below > 0 && (
        <button
          onClick={scrollDown}
          className="absolute bottom-0 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-t-xl shadow-lg border border-b-0 border-border-default text-sm font-medium text-teal hover:bg-teal/5 transition-colors"
        >
          <ChevronDown className="w-4 h-4" />
          <span>{hiddenEventCounts.below} later</span>
        </button>
      )}

      {/* Day Headers */}
      <div className="flex border-b border-border-default bg-bg-surface sticky top-0 z-20">
        <div className="w-16 shrink-0" /> {/* Time column spacer */}
        {days.map((day) => {
          const weekend = isWeekend(day);
          const today = isToday(day);
          return (
            <button
              key={day.toISOString()}
              onClick={() => onDayClick(day)}
              className={`flex-1 p-3 text-center hover:bg-bg-secondary transition-colors border-l border-border-light ${
                today ? 'bg-teal/5' : weekend ? 'bg-bg-warm' : ''
              }`}
            >
              <div className="text-xs lg:text-sm font-semibold text-text-secondary uppercase tracking-wide">
                {format(day, 'EEE')}
              </div>
              <div
                className={`text-lg lg:text-xl font-semibold mt-1 ${
                  today
                    ? 'w-9 h-9 lg:w-11 lg:h-11 rounded-xl bg-teal text-white flex items-center justify-center mx-auto shadow-teal'
                    : 'text-text-primary'
                }`}
              >
                {format(day, 'd')}
              </div>
            </button>
          );
        })}
      </div>

      {/* All-day events section */}
      {hasAllDayEvents && (
        <div className="flex border-b border-border-default bg-bg-surface">
          <div className="w-16 shrink-0 p-2 text-right text-xs font-semibold text-text-secondary uppercase tracking-wide">
            All day
          </div>
          {days.map((day) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const allDayEvents = allDayEventsByDay[dateKey] || [];
            const weekend = isWeekend(day);
            const today = isToday(day);
            return (
              <div
                key={day.toISOString()}
                className={`flex-1 p-1.5 border-l border-border-light min-h-[40px] ${
                  today ? 'bg-teal/5' : weekend ? 'bg-bg-warm' : ''
                }`}
              >
                {allDayEvents.slice(0, 2).map((event) => {
                  const isMultiDay = isMultiDayEvent(event);
                  return (
                    <div
                      key={event.id}
                      className={`text-xs p-1.5 rounded-lg mb-1 cursor-pointer hover:shadow-md bg-white shadow-sm border border-border-light transition-shadow ${isEventPast(event) ? 'event-card--past' : ''}`}
                      style={getEventBorderStyle(event.members)}
                      title={event.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event, event.members);
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {event.isShared && <MemberDotsStack members={event.members} maxVisible={2} size="sm" />}
                        <span className="line-clamp-2 break-words font-medium">{event.title}</span>
                      </div>
                      {isMultiDay && (
                        <span className="text-text-muted text-xs mt-0.5 block">
                          {formatMultiDayRange(event)}
                        </span>
                      )}
                    </div>
                  );
                })}
                {allDayEvents.length > 2 && (
                  <div className="text-xs text-text-muted px-1 font-medium">
                    +{allDayEvents.length - 2} more
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Time Grid */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <div className="relative">
          {/* Hour rows */}
          {hours.map((hour) => (
            <div key={hour} className="flex border-b border-border-light">
              <div className="w-16 shrink-0 p-2 text-right text-xs text-text-secondary">
                {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
              </div>
              {days.map((day) => {
                const weekend = isWeekend(day);
                const today = isToday(day);
                return (
                  <div
                    key={day.toISOString()}
                    className={`flex-1 border-l border-border-light ${
                      today ? 'bg-teal/5' : weekend ? 'bg-bg-warm' : ''
                    }`}
                    style={{ height: HOUR_HEIGHT }}
                  />
                );
              })}
            </div>
          ))}

          {/* Events Overlay */}
          <div className="absolute inset-0 left-16 pointer-events-none">
            <div className="flex h-full">
              {days.map((day) => {
                const dateKey = format(day, 'yyyy-MM-dd');
                // Get events with lane assignments
                const timedEvents = timedEventsWithLanes.eventsWithLanes[dateKey] || [];
                const overflowCount = timedEventsWithLanes.overflowCounts[dateKey] || 0;

                return (
                  <div
                    key={day.toISOString()}
                    className="flex-1 relative border-l border-transparent"
                  >
                    {timedEvents.map((event) => {
                      const style = getEventStyle(event, day);
                      if (!style) return null; // Shouldn't happen after filtering

                      // Calculate horizontal position based on lane assignment
                      const { laneIndex, totalLanes } = event;
                      const columnWidth = 100 / totalLanes; // Percentage
                      const leftPercent = laneIndex * columnWidth;
                      const widthPercent = columnWidth;

                      // Add small gap between columns (in pixels)
                      const gap = totalLanes > 1 ? 2 : 0;

                      return (
                        <div
                          key={event.id}
                          className={`absolute rounded-lg overflow-hidden cursor-pointer pointer-events-auto bg-white shadow-sm border-y border-r border-border-light hover:shadow-lg hover:z-10 transition-all duration-200 ${isEventPast(event) ? 'event-card--past' : ''}`}
                          style={{
                            top: style.top,
                            height: style.height,
                            left: `calc(${leftPercent}% + ${gap}px)`,
                            width: `calc(${widthPercent}% - ${gap * 2}px)`,
                            ...getEventBorderStyle(event.members),
                          }}
                          title={`${event.title} - ${formatTime(event.startTime)}`}
                          onClick={() => onEventClick(event, event.members)}
                        >
                          <div className="p-1.5 text-xs h-full flex flex-col">
                            <div className="flex items-center gap-1 min-w-0">
                              {event.isShared && totalLanes <= 2 && (
                                <MemberDotsStack members={event.members} maxVisible={2} size="sm" />
                              )}
                              <span className="font-semibold line-clamp-2 break-words min-w-0 flex-1">
                                {event.title}
                              </span>
                            </div>
                            {style.height >= 40 && (
                              <div className="text-text-secondary truncate mt-0.5">
                                {formatTime(event.startTime, false)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Overflow indicator - click to expand to Day view */}
                    {overflowCount > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDayClick(day);
                        }}
                        className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-teal text-white text-xs font-semibold rounded-full shadow-lg hover:bg-teal-dark transition-colors pointer-events-auto z-10"
                        title={`View ${overflowCount} more event${overflowCount > 1 ? 's' : ''} in Day view`}
                      >
                        +{overflowCount} more
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Current Time Indicator - spans across all columns when today is visible */}
          {todayIsVisible && currentTimeTop !== null && (
            <div
              className="absolute left-16 right-0 h-0.5 bg-coral z-20 pointer-events-none shadow-coral"
              style={{ top: currentTimeTop }}
            >
              <div className="absolute -left-1 -top-1.5 w-3 h-3 rounded-full bg-coral shadow-coral" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Month View Component
function MonthView({
  days,
  currentDate,
  members,
  eventsByDate,
  onDayClick,
  onEventClick,
}: {
  days: Date[];
  currentDate: Date;
  members: Member[];
  eventsByDate: Record<string, Record<string, CalendarEvent[]>>;
  onDayClick: (date: Date) => void;
  onEventClick: (event: CalendarEvent, members?: Member[]) => void;
}) {
  const weekDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="h-full flex flex-col p-6">
      {/* Week day headers */}
      <div className="grid grid-cols-7 mb-3">
        {weekDayNames.map((name) => (
          <div
            key={name}
            className="text-center text-xs font-semibold text-text-secondary uppercase tracking-wide py-2"
          >
            {name}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 grid grid-cols-7 grid-rows-6 gap-2">
        {days.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayEvents = eventsByDate[dateKey] || {};
          // Deduplicate events across members
          const allEvents = deduplicateEvents(dayEvents, members);
          const isCurrentMonth = isSameMonth(day, currentDate);

          return (
            <button
              key={day.toISOString()}
              onClick={() => onDayClick(day)}
              className={`p-2 rounded-xl border transition-all duration-200 text-left ${
                isToday(day)
                  ? 'border-teal bg-teal/5 shadow-sm'
                  : isCurrentMonth
                    ? 'border-border-light hover:border-teal/30 hover:bg-bg-secondary hover:shadow-sm'
                    : 'border-transparent bg-bg-secondary/50 text-text-muted'
              }`}
            >
              <div
                className={`text-sm lg:text-lg lg:font-bold mb-1.5 ${
                  isToday(day)
                    ? 'w-7 h-7 lg:w-9 lg:h-9 rounded-lg bg-teal text-white flex items-center justify-center shadow-teal'
                    : ''
                }`}
              >
                {format(day, 'd')}
              </div>

              {/* Event dots/previews */}
              <div className="space-y-1">
                {allEvents.slice(0, 3).map((event) => (
                  <div
                    key={event.id}
                    className={`text-xs rounded-md px-1.5 py-0.5 cursor-pointer bg-white shadow-sm border border-border-light hover:shadow-md transition-shadow flex items-center gap-0.5 ${isEventPast(event) ? 'event-card--past' : ''}`}
                    style={getEventBorderStyle(event.members)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event, event.members);
                    }}
                  >
                    {event.isShared && (
                      <MemberDotsStack members={event.members} maxVisible={2} size="sm" />
                    )}
                    <span className="truncate lg:line-clamp-2 lg:text-sm font-medium">{event.title}</span>
                  </div>
                ))}
                {allEvents.length > 3 && (
                  <div className="text-xs text-teal font-medium">
                    +{allEvents.length - 3} more
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
